import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { ArrowLeft, Shield, Loader2, Search, RefreshCw, ChevronDown, ChevronRight, ChevronLeft, AlertTriangle, AlertOctagon, Info } from 'lucide-react';

import { formatDateTime as fmt } from '../../lib/format';

const SEVERITY_BADGE = {
  critical: { cls: 'bg-rose-100 text-rose-800 border border-rose-300', icon: AlertOctagon, label: 'CRITICAL' },
  warning: { cls: 'bg-amber-100 text-amber-800 border border-amber-300', icon: AlertTriangle, label: 'WARN' },
  info: { cls: 'bg-slate-100 text-slate-600 border border-slate-200', icon: Info, label: 'INFO' },
};

const SEVERITY_BORDER = {
  critical: 'border-l-4 border-l-rose-500',
  warning: 'border-l-4 border-l-amber-400',
  info: 'border-l-4 border-l-transparent',
};

const CHANGE_TYPE_LABELS = {
  employee: 'Employee', payroll: 'Payroll', leave: 'Leave', settings: 'Settings',
  procurement: 'Procurement', deletion: 'Deletion', login: 'Login', quality: 'Quality', finance: 'Finance',
};

const EMPTY_FILTERS = {
  user_name: '', actor_department: '', actor_role: '',
  employee_name: '', employee_id: '', change_type: '',
  action: '', entity_type: '', severity: '', from_date: '', to_date: '',
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function DiffViewer({ diff, before, after }) {
  const hasDiff = diff && Object.keys(diff).length > 0;
  if (!hasDiff && !before && !after) return <span className="text-slate-400 text-xs italic">No payload captured</span>;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-2 text-xs">
      {hasDiff ? (
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-display">
              <th className="text-left py-1 pr-3 w-40">Field</th>
              <th className="text-left py-1 pr-3">Before</th>
              <th className="text-left py-1">After</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(diff).map(([field, val]) => (
              <tr key={field} className="border-t border-slate-200 bg-amber-50/40">
                <td className="py-1 pr-3 font-mono text-[11px] font-semibold">{field}</td>
                <td className="py-1 pr-3 font-mono text-[11px] text-rose-700">{JSON.stringify(val.from)}</td>
                <td className="py-1 font-mono text-[11px] text-emerald-700">{JSON.stringify(val.to)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <pre className="text-[11px] font-mono whitespace-pre-wrap text-slate-600">{JSON.stringify(after || before, null, 2)}</pre>
      )}
    </div>
  );
}

export default function AuditLog() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    rows: [], total: 0, page: 1, page_size: 50, total_pages: 1,
    filters: { actions: [], entity_types: [], severities: [] },
    distinct_user_names: [], distinct_departments: [], distinct_roles: [], distinct_change_types: [],
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    apiGet(`/settings/audit-log?${params.toString()}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [filters, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Any filter change resets to page 1.
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const clearAll = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  const start = data.total === 0 ? 0 : (data.page - 1) * data.page_size + 1;
  const end = Math.min(data.page * data.page_size, data.total);

  return (
    <div className="p-6 md:p-8" data-testid="audit-log-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Audit Log" title="Audit Log" subtitle={`Full system audit trail — ${data.total.toLocaleString()} events logged.`} icon={Shield} />

      <div className="bg-white border border-slate-200 rounded-sm p-4 mt-6 space-y-3">
        {/* Row 1 — Actor (who made the change) */}
        <div className="flex flex-wrap items-end gap-3">
          <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-400 w-16 pb-2">Actor</span>
          <Field label="Actor name">
            <input value={filters.user_name} onChange={(e) => setFilter('user_name', e.target.value)} placeholder="e.g. Omkar" list="actor-names" className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-48" data-testid="filter-actor-name" />
            <datalist id="actor-names">{(data.distinct_user_names || []).map((n) => <option key={n} value={n} />)}</datalist>
          </Field>
          <Field label="Actor department">
            <select value={filters.actor_department} onChange={(e) => setFilter('actor_department', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-44 bg-white" data-testid="filter-actor-dept">
              <option value="">All departments</option>
              {(data.distinct_departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Actor role">
            <select value={filters.actor_role} onChange={(e) => setFilter('actor_role', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-40 bg-white" data-testid="filter-actor-role">
              <option value="">All roles</option>
              {(data.distinct_roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
        </div>

        {/* Row 2 — Subject (what was changed) */}
        <div className="flex flex-wrap items-end gap-3">
          <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-400 w-16 pb-2">Subject</span>
          <Field label="Employee name">
            <input value={filters.employee_name} onChange={(e) => setFilter('employee_name', e.target.value)} placeholder="affected employee" className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-48" data-testid="filter-emp-name" />
          </Field>
          <Field label="Employee ID">
            <input value={filters.employee_id} onChange={(e) => setFilter('employee_id', e.target.value)} placeholder="e.g. EEPC000123" className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-44" data-testid="filter-emp-id" />
          </Field>
          <Field label="Change type">
            <select value={filters.change_type} onChange={(e) => setFilter('change_type', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-44 bg-white" data-testid="filter-change-type">
              <option value="">All change types</option>
              {(data.distinct_change_types || []).map((c) => <option key={c} value={c}>{CHANGE_TYPE_LABELS[c] || c}</option>)}
            </select>
          </Field>
        </div>

        {/* Row 3 — Existing filters */}
        <div className="flex flex-wrap items-end gap-3">
          <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-400 w-16 pb-2">More</span>
          <Field label="Action">
            <input value={filters.action} onChange={(e) => setFilter('action', e.target.value)} placeholder="e.g. employee.updated" className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-48" data-testid="filter-action" />
          </Field>
          <Field label="Entity">
            <select value={filters.entity_type} onChange={(e) => setFilter('entity_type', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-40 bg-white" data-testid="filter-entity">
              <option value="">All entities</option>
              {data.filters.entity_types.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select value={filters.severity} onChange={(e) => setFilter('severity', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-32 bg-white" data-testid="filter-severity">
              <option value="">All</option>
              {(data.filters.severities || ['info', 'warning', 'critical']).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="From">
            <input type="date" value={filters.from_date} onChange={(e) => setFilter('from_date', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-40" data-testid="filter-from" />
          </Field>
          <Field label="To">
            <input type="date" value={filters.to_date} onChange={(e) => setFilter('to_date', e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm w-40" data-testid="filter-to" />
          </Field>
          <button onClick={clearAll} className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1 font-display font-bold uppercase tracking-wider" data-testid="clear-filters">
            <Search size={11} /> Clear
          </button>
          <button onClick={load} className="text-xs px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-indigo-50 flex items-center gap-1 font-display font-bold uppercase tracking-wider ml-auto" data-testid="refresh-audit">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-4">
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading audit events…</div>
        ) : data.rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">No audit events match the current filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
              <tr>
                <th className="text-left py-2.5 px-3 w-8"></th>
                <th className="text-left py-2.5 px-3 w-44">Timestamp</th>
                <th className="text-left py-2.5 px-3 w-24">Severity</th>
                <th className="text-left py-2.5 px-3">Actor</th>
                <th className="text-left py-2.5 px-3">Action</th>
                <th className="text-left py-2.5 px-3">Entity</th>
                <th className="text-left py-2.5 px-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const sev = SEVERITY_BADGE[r.severity] || SEVERITY_BADGE['info'];
                const SevIcon = sev?.icon ?? Info;
                const canExpand = (r.diff && Object.keys(r.diff).length > 0) || r.before || r.after;
                const isOpen = expanded[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`border-t border-slate-100 hover:bg-slate-50/60 ${SEVERITY_BORDER[r.severity || 'info']} ${r.severity === 'critical' ? 'bg-rose-50/40' : ''}`} data-testid={`audit-row-${r.id}`}>
                      <td className="py-2 px-3">
                        {canExpand && (
                          <button onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))} className="text-slate-400 hover:text-[#3b4872]" data-testid={`audit-expand-${r.id}`}>
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">{fmt(r.timestamp)}</td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${sev.cls} text-[10px] font-display font-bold uppercase tracking-wider rounded-sm`}>
                          <SevIcon size={10} /> {sev.label}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-semibold text-sm">{r.user_name || r.user_email}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.user_email}{r.user_role ? ` · ${r.user_role}` : ''}{r.user_department ? ` · ${r.user_department}` : ''}</div>
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-block px-2 py-0.5 bg-indigo-50 text-[#3b4872] text-[11px] font-mono rounded-sm">{r.action}</span>
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {r.entity_type ? <><span className="font-semibold">{r.entity_type}</span>{r.entity_id ? <span className="text-slate-500 font-mono"> · {r.entity_id.slice(0, 14)}</span> : null}</> : '—'}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-600">{r.details || '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={7} className="px-12 pb-3">
                          <DiffViewer diff={r.diff} before={r.before} after={r.after} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm" data-testid="audit-pagination">
        <div className="text-slate-500 text-xs">
          Showing <strong>{start.toLocaleString()}–{end.toLocaleString()}</strong> of <strong>{data.total.toLocaleString()}</strong> entries
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 flex items-center gap-1">
            Rows
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-slate-300 rounded-sm px-2 py-1 text-xs bg-white" data-testid="page-size">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1} className="px-2.5 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 text-xs" data-testid="page-prev">
            <ChevronLeft size={13} /> Prev
          </button>
          <span className="text-xs text-slate-600 font-display font-bold">Page {data.page} / {data.total_pages}</span>
          <button onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={data.page >= data.total_pages} className="px-2.5 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 text-xs" data-testid="page-next">
            Next <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
